/**
 * LearnerLicencePrompt — calm sticky-but-dismissible licence nudge.
 *
 * Sources of truth (May 14, 2026):
 *   • Visibility decided by the BACKEND. GET /api/licence/me returns
 *     `{record, prompt_visible}`. The frontend never invents a
 *     dismissal — it just respects what the server says.
 *   • Dismissal POSTs to /api/licence/dismiss-prompt → hidden for
 *     7 days OR until the licence status changes (e.g. verified ->
 *     expired). Real signals are never silenced by old dismissals.
 *
 * No localStorage state, no per-tab logic — one canonical decision
 * from one canonical endpoint.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, ShieldQuestion, ChevronRight, X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LearnerLicencePrompt() {
  const [record, setRecord] = useState(null);
  const [promptVisible, setPromptVisible] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/licence/me`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        setRecord(data?.record ?? null);
        setPromptVisible(data?.prompt_visible !== false);
      })
      .catch(() => {
        // Endpoint may be unavailable for non-learners — stay silent.
        if (!cancelled) setRecord(null);
      })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const onDismiss = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (dismissing) return;
    setDismissing(true);
    try {
      await axios.post(
        `${API}/api/licence/dismiss-prompt`,
        {},
        { withCredentials: true },
      );
      setPromptVisible(false);
    } catch {
      // Silent — the prompt will simply re-appear next mount.
    } finally {
      setDismissing(false);
    }
  };

  if (!loaded) return null;
  if (!promptVisible) return null;

  const status = record?.status || "missing";
  const tone = {
    verified: { bg: "bg-emerald-50 dark:bg-emerald-900/20", border: "border-emerald-200 dark:border-emerald-800", text: "text-emerald-700 dark:text-emerald-300", icon: ShieldCheck },
    pending:  { bg: "bg-amber-50 dark:bg-amber-900/20",     border: "border-amber-200 dark:border-amber-800",    text: "text-amber-700 dark:text-amber-300",    icon: ShieldQuestion },
    expired:  { bg: "bg-red-50 dark:bg-red-900/20",         border: "border-red-200 dark:border-red-800",        text: "text-red-700 dark:text-red-300",        icon: ShieldAlert },
    missing:  { bg: "bg-slate-50 dark:bg-slate-800/40",     border: "border-slate-200 dark:border-slate-700",    text: "text-slate-700 dark:text-slate-300",    icon: ShieldQuestion },
  }[status] || { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", icon: ShieldQuestion };

  const ToneIcon = tone.icon;
  const title = {
    verified: "Driving licence verified",
    pending:  "Driving licence check pending",
    expired:  "Driving licence expired",
    missing:  "Check your driving licence",
  }[status] || "Check your driving licence";

  const subtitle = {
    verified: "Your details are confirmed and on file.",
    pending:  "We're reviewing your uploaded photo.",
    expired:  "Please re-upload before your next lesson.",
    missing:  "Upload a quick photo to confirm you can drive on UK roads.",
  }[status] || "Tap to check.";

  return (
    <div
      data-testid="learner-licence-prompt-row"
      className={`relative flex items-center gap-3 w-full p-4 rounded-2xl border-2 ${tone.bg} ${tone.border}`}
    >
      <Link
        to="/licence"
        data-testid="learner-licence-prompt"
        data-status={status}
        className="flex items-center gap-3 flex-1 min-w-0 pr-7"
      >
        <ToneIcon className={`w-5 h-5 ${tone.text} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold ${tone.text} truncate`}>
            {title}
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-400 truncate">
            {subtitle}
          </p>
        </div>
        <ChevronRight className={`w-5 h-5 ${tone.text} flex-shrink-0`} />
      </Link>
      <button
        type="button"
        data-testid="learner-licence-prompt-dismiss"
        onClick={onDismiss}
        disabled={dismissing}
        aria-label="Hide for 7 days"
        className="absolute top-2 right-2 p-1.5 rounded-lg hover:bg-white/60 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-50 transition"
      >
        {dismissing
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : <X className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}
